import React, { useEffect, useMemo, useRef, useState } from "react";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";

const PortalV2Select = ({
  label,
  value,
  options,
  onChange,
  className = "",
  layout = "inline",
  required = false,
  placeholder = "Select",
  searchable = false,
  searchPlaceholder = "Search…",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const isField = layout === "field";

  const selected = options.find((opt) => opt.value === value);

  const filteredOptions = useMemo(() => {
    if (!searchable || !search.trim()) return options;
    const needle = search.trim().toLowerCase();
    return options.filter((opt) => String(opt.label || "").toLowerCase().includes(needle));
  }, [options, searchable, search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && searchable && searchRef.current) {
      searchRef.current.focus();
    }
    if (!open) setSearch("");
  }, [open, searchable]);

  const optionNodes = filteredOptions.length ? (
    filteredOptions.map((opt) => (
      <li key={String(opt.value)} role="option" aria-selected={opt.value === value}>
        <button
          type="button"
          className={`portal-v2-select__option ${opt.value === value ? "is-selected" : ""}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            onChange(opt.value);
            setOpen(false);
            setSearch("");
          }}
        >
          {opt.label}
        </button>
      </li>
    ))
  ) : (
    <li role="presentation">
      <span className="portal-v2-select__option" style={{ cursor: "default", color: "#9ca3af" }}>
        No matches
      </span>
    </li>
  );

  const selectControl = (
    <div className={`portal-v2-select ${isField ? "portal-v2-select--block" : ""} ${open ? "is-open" : ""}`}>
      {!isField && label ? <span className="portal-v2-select__label">{label}</span> : null}
      <button
        type="button"
        className={`portal-v2-select__trigger ${isField ? "portal-v2-select__trigger--block" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`portal-v2-select__value ${!selected ? "is-placeholder" : ""}`}>
          {selected?.label || placeholder}
        </span>
        <KeyboardArrowDownOutlinedIcon className="portal-v2-select__chevron" />
      </button>
      {open &&
        (searchable ? (
          <div
            className={`portal-v2-select__menu ${isField ? "portal-v2-select__menu--block" : ""} portal-v2-select__menu--creatable`}
            role="listbox"
          >
            <div className="portal-v2-select__search-wrap">
              <input
                ref={searchRef}
                type="text"
                className="portal-v2-select__search"
                value={search}
                placeholder={searchPlaceholder}
                onChange={(e) => setSearch(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <ul className="portal-v2-select__options" role="presentation">
              {optionNodes}
            </ul>
          </div>
        ) : (
          <ul
            className={`portal-v2-select__menu ${isField ? "portal-v2-select__menu--block" : ""}`}
            role="listbox"
          >
            {optionNodes}
          </ul>
        ))}
    </div>
  );

  if (isField) {
    return (
      <div className={`portal-v2-select-field ${className}`} ref={rootRef}>
        {label ? (
          <span className="portal-v2-select-field__label">
            {label}
            {required ? <em>*</em> : null}
          </span>
        ) : null}
        {selectControl}
      </div>
    );
  }

  return (
    <div className={`portal-v2-select-wrap ${className}`} ref={rootRef}>
      {selectControl}
    </div>
  );
};

export default PortalV2Select;
